import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { apiFetch, ApiError } from "../../lib/apiFetch";

export type Role = "guest" | "viewer" | "admin" | "super_admin";

export const ROLE_RANK: Record<Role, number> = { guest: 0, viewer: 1, admin: 2, super_admin: 3 };

export const ROLE_LABEL: Record<Role, string> = {
  super_admin: "슈퍼관리자",
  admin: "관리자",
  viewer: "뷰어",
  guest: "게스트",
};

/** ROLE_LABEL 의 영문 — 화면 언어가 en 일 때. */
export const ROLE_LABEL_EN: Record<Role, string> = {
  super_admin: "Super admin",
  admin: "Admin",
  viewer: "Viewer",
  guest: "Guest",
};

export type AuthStatus = "loading" | "authenticated" | "unauthenticated";

type AuthState = {
  status: AuthStatus;
  email: string | null;
  name: string | null;
  picture: string | null;
  role: Role | null;
  error: string | null;
};

type AuthContextValue = AuthState & {
  hasRole: (minRole: Role) => boolean;
  refresh: () => Promise<void>;
};

const INITIAL_STATE: AuthState = {
  status: "loading",
  email: null,
  name: null,
  picture: null,
  role: null,
  error: null,
};

const AuthContext = createContext<AuthContextValue | null>(null);

function normalizeRole(value: unknown): Role {
  return value === "super_admin" || value === "admin" || value === "viewer" || value === "guest"
    ? value
    : "viewer";
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>(INITIAL_STATE);

  const refresh = useCallback(async () => {
    try {
      const data = await apiFetch<{ email?: string; name?: string; picture?: string; role?: unknown }>(
        "/api/auth/verify",
      );
      setState({
        status: "authenticated",
        email: data.email ?? null,
        name: data.name ?? null,
        picture: data.picture ?? null,
        role: normalizeRole(data.role),
        error: null,
      });
    } catch (err) {
      // 실패는 전부 미인증으로 떨어뜨린다 — 기존 동작 그대로다.
      // 여기서 예외를 밖으로 내보내면 status 가 "loading" 에 갇혀 앱이 아예 뜨지 않는다.
      // HTML 응답(라우팅 회귀)이면 apiFetch 가 이미 console.error 를 남기므로 신호는 보존된다.
      const body = err instanceof ApiError ? (err.body as { error?: string } | null) : null;
      setState({
        status: "unauthenticated",
        email: null,
        name: null,
        picture: null,
        role: null,
        error: body?.error ?? null,
      });
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      hasRole: (minRole: Role) =>
        state.status === "authenticated" && ROLE_RANK[state.role ?? "viewer"] >= ROLE_RANK[minRole],
      refresh,
    }),
    [state, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Provider 밖에서도 쓰는 곳(공유 보기 · 테스트 렌더)용 — 없으면 null(권한 없음으로 본다) */
export function useOptionalAuth(): AuthContextValue | null {
  return useContext(AuthContext);
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider.");
  return context;
}
